<template>
  <main :class="mainClass">
    <Topbar v-if="showTopbar" />
    <TopbarCourse v-if="showTopbarCourse" />
    <section :class="sectionClass">
      <router-view v-slot="{ Component }" :key="$route.path">
        <transition name="scale" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </section>
    <FooterComponent v-if="meta.disableFooter()" />
    <Toast id="toast-message" position="bottom-left" />
  </main>
</template>

<script lang="ts">
import {
  createDetector,
  MediaPipeFaceMeshMediaPipeModelConfig,
  SupportedModels,
} from "@tensorflow-models/face-landmarks-detection";
import { defineComponent } from "vue";
import { RouteLocationNormalized } from "vue-router";
import { AttentionService } from "./components/Attention/attention.service";
import { Roles } from "./models/user.model";
import { useAuthStore } from "./store/auth";
import { useThemeStore } from "./store/theme";
import { RouteMeta } from "./utils/metas.util";
import { getUserFromStorage } from "./utils/user-store.util";

export default defineComponent({
  name: "App",
  computed: {
    meta(): RouteMeta {
      return new RouteMeta();
    },
    mainClass(): string {
      return `dark:bg-black dark:text-white ${
        this.meta.disableFooter() ? "h-screen" : ""
      }`;
    },
    sectionClass(): string {
      return `${
        this.meta.hasContainer() ? "container" : ""
      } mx-auto min-h-full overflow-x-hidden`;
    },
    showTopbar(): boolean {
      return !this.meta.isPageCourse() && !this.meta.isPageCourseTraditional();
    },
    showTopbarCourse(): boolean {
      return this.meta.isPageCourse() || this.meta.isPageCourseTraditional();
    },
  },
  async mounted() {
    this.initializeApp();
  },
  watch: {
    $route(to) {
      window.scrollTo(0, 0);
      this.setTopbarForForum(to);
    },
  },
  methods: {
    async initializeApp(): Promise<void> {
      this.detectFaces();
      const authStore = useAuthStore;
      const themeStore = useThemeStore;

      authStore.loadTokensFromStorage();
      themeStore.getTheme();
      themeStore.getLogo();
      await themeStore.getPartners();
    },
    async detectFaces(): Promise<void> {
      const detectorConfig: MediaPipeFaceMeshMediaPipeModelConfig = {
        runtime: "mediapipe",
        solutionPath: "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh",
        refineLandmarks: false,
      };

      const detector = await createDetector(
        SupportedModels.MediaPipeFaceMesh,
        detectorConfig
      );

      AttentionService.detectorFaces.next(detector);
    },
    setTopbarForForum(to: RouteLocationNormalized): void {
      if (to.meta.groupRouteFrom === "forum") {
        to.meta.pageCourse = Roles.STUDENT === getUserFromStorage()?.role;
      }
    },
  },
});
</script>

<style src="./app.scss" lang="scss" />
